import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const locationLinks = [
  { label: "Birmingham", slug: "birmingham-rubbish-removal", icon: "ri-building-2-line" },
  { label: "Solihull", slug: "solihull-rubbish-removal", icon: "ri-home-2-line" },
  { label: "Droitwich Spa", slug: "droitwich-spa-rubbish-removal", icon: "ri-map-pin-line" },
  { label: "Bromsgrove", slug: "bromsgrove-rubbish-removal", icon: "ri-community-line" },
  { label: "Redditch", slug: "redditch-rubbish-removal", icon: "ri-building-4-line" },
];

interface NavbarProps {
  onBookNow: () => void;
}

export default function Navbar({ onBookNow }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [locationsOpen, setLocationsOpen] = useState(false);
  const servicesCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locationsCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const openServices = () => {
    if (servicesCloseTimer.current) clearTimeout(servicesCloseTimer.current);
    setServicesOpen(true);
  };
  const closeServices = () => {
    servicesCloseTimer.current = setTimeout(() => setServicesOpen(false), 180);
  };

  const openLocations = () => {
    if (locationsCloseTimer.current) clearTimeout(locationsCloseTimer.current);
    setLocationsOpen(true);
  };
  const closeLocations = () => {
    locationsCloseTimer.current = setTimeout(() => setLocationsOpen(false), 180);
  };

  const serviceLinks = [
    { label: "Man & Van Rubbish Removal Service", icon: "ri-truck-line", desc: "Same-day from £99.99", href: "/rubbish-clearance" },
    { label: "Sofa Removal", icon: "ri-sofa-line", desc: "From £73.49", href: "/sofa-removal" },
    { label: "Bin Collection", icon: "ri-recycle-line", desc: "From £64.99", href: "/private-bin-collection" },
    { label: "Furniture Collection", icon: "ri-archive-line", desc: "From £73.49", href: "/furniture-removal" },
    { label: "House Clearance", icon: "ri-home-smile-line", desc: "Full clearance service", href: "/house-clearance" },
    { label: "Appliance Removal", icon: "ri-plug-line", desc: "From £71.74", href: "/appliance-removal" },
  ];

  const textColor = "text-cl-dark";
  const hoverColor = "hover:text-cl-green";

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-cl-dark lg:bg-transparent ${
        scrolled ? "lg:bg-white lg:border-b lg:border-gray-100" : "lg:bg-white/95 lg:backdrop-blur-sm lg:border-b lg:border-gray-100"
      }`}
    >
      {/* Decorative left curve accent — visible only when scrolled */}
      <div
        className={`absolute left-0 top-0 h-full pointer-events-none overflow-hidden transition-opacity duration-500 ${scrolled ? "opacity-100" : "opacity-0"}`}
        style={{ width: "10%" }}
      >
        {/* Back curve — lime bold */}
        <div
          className="absolute left-0 top-0 h-full bg-cl-lime-bold/25"
          style={{ width: "100%", borderRadius: "0 9999px 9999px 0" }}
        />
        {/* Front curve — green */}
        <div
          className="absolute left-0 top-0 h-full bg-cl-green/20"
          style={{ width: "52%", borderRadius: "0 9999px 9999px 0" }}
        />
      </div>
      {/* Top utility bar */}
      {!scrolled && (
        <div className="hidden lg:block w-full bg-cl-lime border-b-2 border-cl-dark/20">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-10 flex items-center justify-between py-1.5">
            <span className="text-xs font-black text-cl-dark truncate">
              Unit 4, Crown Farm, Crown Lane, Wychbold, Droitwich, WR9 0BX
            </span>
            <div className="flex items-center gap-4 ml-auto">
              <a
                href="tel:03335775445"
                className="flex items-center gap-1.5 text-sm font-black text-cl-dark hover:opacity-70 transition-opacity cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-sm"></i>
                03335 775445
              </a>
              <span className="text-cl-dark/30 text-xs">|</span>
              <a
                href="https://cleario.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs font-black text-cl-dark hover:opacity-70 transition-opacity cursor-pointer whitespace-nowrap"
              >
                <i className="ri-global-line text-xs"></i>
                www.cleario.uk
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center h-16 lg:h-20 px-4 lg:px-10 max-w-screen-xl mx-auto bg-cl-dark lg:bg-transparent">
        {/* Logo */}
        <Link to="/" className="cursor-pointer mr-auto flex items-center gap-1">
          <span className="text-2xl font-black tracking-tight text-white lg:text-cl-dark">
            Clear<span className="text-cl-green">io</span>
          </span>
          <span className="w-2.5 h-2.5 rounded-full mb-4 inline-block bg-cl-green border-2 border-white lg:border-cl-dark"></span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-7">
          {/* Services dropdown */}
          <div
            className="relative"
            onMouseEnter={openServices}
            onMouseLeave={closeServices}
          >
            <button
              className={`flex items-center gap-1 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${textColor} ${hoverColor}`}
            >
              Services
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${servicesOpen ? "rotate-180" : ""}`}></i>
            </button>

            {servicesOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[500px] bg-white rounded-2xl p-5 z-50 cartoon-shadow thick-border"
                onMouseEnter={openServices}
                onMouseLeave={closeServices}
              >
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3 px-1">All Services</p>
                <div className="grid grid-cols-2 gap-2">
                  {serviceLinks.map((link) => (
                    <Link
                      key={link.label}
                      to={link.href}
                      className="flex items-center gap-3 p-3 rounded-xl border-2 border-transparent hover:border-cl-green hover:bg-cl-tint transition-all cursor-pointer group"
                    >
                      <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-cl-lime text-cl-green border-2 border-cl-green group-hover:bg-cl-green group-hover:text-white transition-colors flex-shrink-0">
                        <i className={`${link.icon} text-sm`}></i>
                      </span>
                      <div>
                        <p className="text-sm font-bold text-cl-dark group-hover:text-cl-green transition-colors">{link.label}</p>
                        <p className="text-xs text-gray-500">{link.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t-2 border-transparent flex items-center justify-between px-1">
                  <p className="text-xs text-gray-500">Not sure what you need?</p>
                  <Link to="/waste-calculator" className="text-xs font-black text-cl-green hover:underline cursor-pointer whitespace-nowrap">Try the Waste Calculator →</Link>
                </div>
              </div>
            )}
          </div>

          <Link to="/waste-calculator" className={`text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${textColor} ${hoverColor}`}>
            Calculator
          </Link>

          {/* Locations dropdown */}
          <div
            className="relative"
            onMouseEnter={openLocations}
            onMouseLeave={closeLocations}
          >
            <button
              className={`flex items-center gap-1 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${textColor} ${hoverColor}`}
            >
              Locations
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${locationsOpen ? "rotate-180" : ""}`}></i>
            </button>
            {locationsOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[380px] bg-white rounded-2xl p-5 z-50 cartoon-shadow thick-border"
                onMouseEnter={openLocations}
                onMouseLeave={closeLocations}
              >
                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Service Areas</p>
                <div className="grid grid-cols-2 gap-2">
                  {locationLinks.map((loc) => (
                    <Link
                      key={loc.slug}
                      to={`/locations/${loc.slug}`}
                      className="flex items-center gap-3 p-3 rounded-xl border-2 border-transparent hover:border-cl-green hover:bg-cl-tint transition-all cursor-pointer group"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-cl-lime text-cl-green border-2 border-cl-green group-hover:bg-cl-green group-hover:text-white transition-colors flex-shrink-0">
                        <i className={`${loc.icon} text-xs`}></i>
                      </span>
                      <p className="text-sm font-bold text-cl-dark group-hover:text-cl-green transition-colors">{loc.label}</p>
                    </Link>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t-2 border-transparent flex items-center justify-between px-1">
                  <p className="text-xs text-gray-500">Serving West Midlands &amp; surrounding areas</p>
                  <Link to="/locations" className="text-xs font-black text-cl-green hover:underline cursor-pointer whitespace-nowrap">View all →</Link>
                </div>
              </div>
            )}
          </div>

          <Link
            to="/account"
            className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-cl-dark hover:bg-cl-tint hover:border-cl-green transition-all cursor-pointer"
            aria-label="My Account"
          >
            <i className="ri-user-line text-sm"></i>
          </Link>

          <Link
            to="/book"
            className={`font-black text-sm px-6 py-2.5 rounded-full whitespace-nowrap transition-all bg-cl-lime-bold text-white cartoon-btn`}
          >
            Book Now
          </Link>
        </div>

        {/* Mobile right side: phone + Book Now + burger */}
        <div className="lg:hidden flex items-center gap-2">
          <a
            href="tel:03335775445"
            aria-label="Call us"
            className="w-9 h-9 flex items-center justify-center rounded-xl text-white active:bg-white/10 transition-colors"
          >
            <i className="ri-phone-fill text-cl-green text-lg"></i>
          </a>
          <Link
            to="/book"
            className="bg-cl-green text-white font-black text-xs px-4 py-2 rounded-full whitespace-nowrap"
            style={{ boxShadow: "0 3px 0 #4a8a0d" }}
          >
            Book Now
          </Link>
          <button
            className="w-9 h-9 flex items-center justify-center cursor-pointer rounded-xl transition-colors text-white active:bg-white/10"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-3-line"} text-xl font-black`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu — navy panel */}
      {menuOpen && (
        <div className="lg:hidden bg-cl-dark border-t-2 border-white/10 px-5 py-5 flex flex-col gap-4">
          <p className="text-xs font-black text-white/40 uppercase tracking-widest">Services</p>
          <div className="grid grid-cols-2 gap-2">
            {serviceLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 p-2.5 rounded-xl border border-white/10 bg-white/5 active:bg-white/10 cursor-pointer"
              >
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-cl-green/20 text-cl-green flex-shrink-0 border border-cl-green/40">
                  <i className={`${link.icon} text-xs`}></i>
                </span>
                <span className="text-sm font-bold text-white">{link.label}</span>
              </Link>
            ))}
          </div>
          <div className="border-t border-white/10 pt-3 flex flex-col gap-3">
            <Link
              to="/waste-calculator"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 text-sm font-bold text-white/80 cursor-pointer"
            >
              <i className="ri-calculator-line text-cl-green"></i>
              Waste Calculator
            </Link>
          </div>
          <div className="border-t border-white/10 pt-3">
            <p className="text-xs font-black text-white/40 uppercase tracking-widest mb-3">Locations</p>
            <div className="grid grid-cols-2 gap-2">
              {locationLinks.map((loc) => (
                <Link
                  key={loc.slug}
                  to={`/locations/${loc.slug}`}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 p-2.5 rounded-xl border border-white/10 bg-white/5 active:bg-white/10 cursor-pointer"
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-cl-green/20 text-cl-green flex-shrink-0 border border-cl-green/40">
                    <i className={`${loc.icon} text-xs`}></i>
                  </span>
                  <span className="text-sm font-bold text-white">{loc.label}</span>
                </Link>
              ))}
            </div>
            <Link
              to="/locations"
              onClick={() => setMenuOpen(false)}
              className="mt-2 block text-xs font-black text-cl-green cursor-pointer px-1"
            >
              View all locations →
            </Link>
          </div>
          {/* Bottom CTA row */}
          <div className="flex items-center gap-3 pt-1">
            <a
              href="tel:03335775445"
              className="flex-1 flex items-center justify-center gap-2 border-2 border-white/20 text-white font-black text-sm px-4 py-3 rounded-full"
            >
              <i className="ri-phone-fill text-cl-green"></i>
              Call Us
            </a>
            <button
              onClick={onBookNow}
              className="flex-1 bg-cl-green text-white font-black text-sm px-5 py-3 rounded-full"
              style={{ boxShadow: "0 3px 0 #4a8a0d" }}
            >
              Book Now
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}